import { ArrowBack, WarningAmber } from '@mui/icons-material';
import { Alert, Box, Grid, IconButton, Tab, Tabs, Typography } from '@mui/material';
import { addHours } from 'date-fns';
import format from 'date-fns/format';
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import uniqid from 'uniqid';
import { createOrder } from '../../api/order/createOrder';
import ClickPopover from '../../components/ClickPopover';
import PrimaryButton from '../../components/PrimaryButton';
import { StyledPaper, boxShadow } from '../../constants/styles';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import useMedia from '../../utils/useMedia';
import EditDeliveryOrderButton from './Delivery/EditDeliveryOrderButton';
import Order from './Order/Order';
import CloseShiftButton from './ShiftButtons/CloseShiftButton';

type Props = {
  isDelivery?: boolean;
};

const Orders = ({ isDelivery }: Props) => {
  const navigate = useNavigate();
  const { isSm } = useMedia();
  const { lang } = useLocale();
  const { tablesMap, cafeSlug, cafe } = useAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const tableId = searchParams.get('table');
  const pointId = searchParams.get('point');
  const orderId = searchParams.get('order');
  const hasTableMap = tablesMap.enabled && tablesMap.tables.length > 0;

  const { regularOrders, deliveryOrders, worker, reservations, currentPosShift, selfOrderPoints } = useCafe();

  const filteredOrders = useMemo(() => {
    if (isDelivery) {
      return deliveryOrders;
    }
    if (tableId) {
      return regularOrders.filter((order) => order.tableId === tableId && order.type !== 'delivery');
    }
    if (pointId) {
      return regularOrders.filter((order) => order.selfOrderPointId === pointId && order.type !== 'delivery');
    }
    return regularOrders;
  }, [deliveryOrders, isDelivery, pointId, regularOrders, tableId]);

  const filteredReservations = useMemo(
    () =>
      tableId
        ? reservations.filter((r) => r.tableId === tableId && r.time > format(addHours(new Date(), -1), 'HH:mm'))
        : [],
    [reservations, tableId]
  );

  const allowedOrders = useMemo(
    () =>
      worker?.credentials.view_orders_of_other_employees
        ? filteredOrders
        : filteredOrders.filter((o) => o.workerId === worker?.id),
    [filteredOrders, worker?.credentials.view_orders_of_other_employees, worker?.id]
  );

  const setOrderId = useCallback(
    (value: string) => {
      if (pointId) {
        setSearchParams({ point: pointId, order: value });
        return;
      }
      if (tableId) {
        setSearchParams({ table: tableId, order: value });
        return;
      }
      setSearchParams({ order: value });
    },
    [pointId, setSearchParams, tableId]
  );

  const order = useMemo(
    () => allowedOrders.find((order) => order.id === orderId) || allowedOrders[0],
    [allowedOrders, orderId]
  );

  const handleChange = (event: React.SyntheticEvent, newOrderId: string) => {
    setOrderId(newOrderId);
  };

  const createNewOrder = useCallback(async () => {
    if (worker && cafe) {
      const id = uniqid();

      await createOrder({
        id,
        workerId: worker.id,
        workerName: worker.name,
        cafe,
        cafeSlug,
        tableId,
        tableName: (tableId && tablesMap.tables.find((t) => t.id === tableId)?.name) || '',
        delivery: null,
        status: 'processing',
        type: 'regular',
        clientId: '',
        clientName: '',
        discount: 0,
        selfOrderPointId: pointId || null,
      });

      setOrderId(id);
    }
  }, [worker, cafe, cafeSlug, tableId, tablesMap.tables, pointId, setOrderId]);

  const table = useMemo(
    () => (tableId ? tablesMap?.tables.find((table) => table.id === tableId) : null),
    [tableId, tablesMap?.tables]
  );

  const point = useMemo(
    () => (pointId ? selfOrderPoints.find((point) => point.id === pointId) : null),
    [pointId, selfOrderPoints]
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {!hasTableMap && currentPosShift && worker?.credentials.open_close_pos_shifts && <CloseShiftButton />}

          {(point || table || cafe?.tablesMap.enabled) && (
            <IconButton onClick={() => navigate(`/${cafeSlug}/pos`)}>
              <ArrowBack />
            </IconButton>
          )}

          {table && <Typography>{`${t.table[lang]}: ${table.name}`}</Typography>}
          {point && <Typography>{`${t.self_order_point[lang]}: ${point.name}`}</Typography>}
          {isDelivery && cafe?.tablesMap.enabled && <Typography>{t.delivery[lang]}</Typography>}
          {!cafe?.tablesMap.enabled && cafe?.qrMenu?.hasDelivery && (
            <Tabs value={isDelivery ? 'delivery' : 'regular'} sx={{ mb: 1, minHeight: 40 }}>
              <Tab
                label={`${t.in_restaurant[lang]}${regularOrders.length ? ` (${regularOrders.length})` : ''}`}
                value="regular"
                sx={{ py: 0, minHeight: 40 }}
                onClick={isDelivery ? () => navigate(`/${cafeSlug}/pos/`) : undefined}
              />
              <Tab
                label={`${t.delivery[lang]}${deliveryOrders.length ? ` (${deliveryOrders.length})` : ''}`}
                value="delivery"
                sx={{ py: 0, minHeight: 40 }}
                onClick={isDelivery ? undefined : () => navigate(`/${cafeSlug}/pos/delivery/`)}
              />
            </Tabs>
          )}
        </Box>

        {filteredReservations.length > 0 && (
          <ClickPopover
            content={
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="h6">{t.booked[lang]}:</Typography>
                {filteredReservations.map((r) => (
                  <Typography key={r.id}>
                    {r.time} - {r.name}
                  </Typography>
                ))}
              </Box>
            }
          >
            {isSm && <WarningAmber color="warning" sx={{ mt: 1 }} />}
            {!isSm && <Alert severity="warning">{t.booking[lang]}</Alert>}
          </ClickPopover>
        )}

        {!isDelivery && (
          <PrimaryButton onClick={createNewOrder} disabled={!worker?.credentials.create_and_edit_own_orders}>
            {t.new_order[lang]}
          </PrimaryButton>
        )}
        {isDelivery && <EditDeliveryOrderButton>{t.new_order[lang]}</EditDeliveryOrderButton>}
      </Box>

      {allowedOrders.length > 0 && (
        <Grid container>
          <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
            {!isSm && <Tab label={`${t.order[lang]}:`} disabled />}
            {order && (
              <Tabs
                value={order.id}
                onChange={handleChange}
                textColor="secondary"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ ml: isSm ? 2 : 0, '& .MuiTabs-indicator': { display: 'none' }, zIndex: 1 }}
              >
                {allowedOrders.map((item) => (
                  <Tab
                    key={item.id}
                    label={`№${item.number}`}
                    iconPosition="start"
                    value={item.id}
                    sx={{
                      bgcolor: item.id === order.id ? (t) => t.palette.background.paper : 'none',
                      color: (t) => (item.id === order.id ? t.palette.secondary.main : t.palette.text.secondary),
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8,
                      minHeight: 48,
                      boxShadow: item.id === order.id ? boxShadow : undefined,
                    }}
                  />
                ))}
              </Tabs>
            )}
          </Grid>
        </Grid>
      )}

      {order && <Order order={order} />}

      {allowedOrders.length === 0 && (
        <StyledPaper isCard sx={{ mt: 3 }}>
          <Box sx={{ textAlign: 'center', px: 5, py: 8 }}>
            <Typography sx={{ color: (t) => t.palette.text.disabled, mb: 2 }}>{t.there_is_nothing[lang]}</Typography>

            {!isDelivery && (
              <PrimaryButton
                variant="contained"
                onClick={createNewOrder}
                disabled={!worker?.credentials.create_and_edit_own_orders}
              >
                {t.new_order[lang]}
              </PrimaryButton>
            )}
          </Box>
        </StyledPaper>
      )}
    </Box>
  );
};

export default Orders;
